import { Link } from '@tanstack/react-router';
import {
  type ColumnFiltersState,
  createColumnHelper,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { AlertCircle, CircleArrowLeft } from 'lucide-react';
import { type Dispatch, type SetStateAction, useMemo, useState } from 'react';
import type { DateRange } from 'react-day-picker';

import { useDashboardFailedPaymentsOverview } from '~entities/payment/api/dashboard-failed-payments-overview';
import {
  type FailedPaymentDetail,
  useReportingFailedPayments,
} from '~entities/payment/api/reporting-failed-payments';

import { useGetCurrency } from '~shared/api/currency-api';
import { DatePickerWithRange } from '~shared/components/date-picker-with-range';
import {
  EnrollioTable,
  EnrollioTableColumnToggle,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { Alert, AlertDescription, AlertTitle } from '~shared/shadcn/ui/alert';
import { Badge } from '~shared/shadcn/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';

export function FailedAndRefunded() {
  const { data: currencyDetails } = useGetCurrency();
  const currencySymbol = currencyDetails?.symbol ?? '$';

  // Use a date range instead of selectedMonth
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    to: new Date(),
  });

  const {
    data: overview,
    isLoading,
    isError,
  } = useDashboardFailedPaymentsOverview();

  const startDateStr = dateRange?.from
    ? format(dateRange.from, 'yyyy-MM-dd')
    : '';
  const endDateStr = dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : '';

  const reportingParams = useMemo(
    () =>
      ({
        startDate: startDateStr,
        endDate: endDateStr,
        format: 'json',
      }) as { startDate: string; endDate: string; format: string },
    [startDateStr, endDateStr],
  );

  const {
    data: failedPaymentsData,
    isLoading: isFailedPaymentsLoading,
    isError: isFailedPaymentsError,
  } = useReportingFailedPayments(reportingParams);

  const paymentsInRange =
    failedPaymentsData && typeof failedPaymentsData !== 'string'
      ? failedPaymentsData.failedPaymentDetails
      : [];

  const totalFailedCountInRange = paymentsInRange.length;
  const totalFailedAmountInRange =
    failedPaymentsData && typeof failedPaymentsData !== 'string'
      ? failedPaymentsData.totalFailedAmount
      : 0;

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isError || !overview) {
    return <div>Error loading failed payments overview.</div>;
  }

  // Remove selectedMonth and related logic
  // Use the selected date range for summary cards and table
  // const selectedFrom = dateRange?.from;
  // const selectedTo = dateRange?.to;
  // const selectedMonthLabel =
  //   selectedFrom && selectedTo
  //     ? `${format(selectedFrom, 'MMM yyyy')} - ${format(selectedTo, 'MMM yyyy')}`
  //     : '';

  return (
    <div className='space-y-6'>
      <Link to='/payment/payment-dashboard' className='flex items-center gap-2'>
        <CircleArrowLeft className='size-6' />
        <h2 className='text-2xl font-semibold'>Failed & Refunded</h2>
      </Link>

      <Alert variant='destructive'>
        <AlertCircle className='size-4' />
        <AlertTitle>Attention Required</AlertTitle>
        <AlertDescription>
          {overview.attentionRequiredCount} Failed Payments Need Attention
        </AlertDescription>
      </Alert>

      <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
        <Card>
          <CardHeader className='pb-2'>
            <CardTitle>Total Failed In Range</CardTitle>
            <CardDescription>
              Number of failed payments in selected range
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className='text-3xl font-bold'>
              {isFailedPaymentsLoading
                ? '...'
                : isFailedPaymentsError
                  ? '—'
                  : `${totalFailedCountInRange} payments`}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className='pb-2'>
            <CardTitle>Total Amount Failed</CardTitle>
            <CardDescription>
              Sum of all failed payments in selected range
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className='text-3xl font-bold'>
              {isFailedPaymentsLoading
                ? '...'
                : isFailedPaymentsError
                  ? '—'
                  : `${currencySymbol}${totalFailedAmountInRange.toLocaleString(
                      undefined,
                      {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      },
                    )}`}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Failed Payments Details</CardTitle>
          <CardDescription>View failed payments by date range</CardDescription>
        </CardHeader>
        <CardContent>
          <FailedPaymentsTable
            dateRange={dateRange}
            setDateRange={setDateRange}
            currencySymbol={currencySymbol}
          />
        </CardContent>
      </Card>
    </div>
  );
}

const failedPaymentColumnHelper = createColumnHelper<FailedPaymentDetail>();

function FailedPaymentsTable({
  dateRange,
  setDateRange,
  currencySymbol,
}: {
  dateRange: DateRange | undefined;
  setDateRange: Dispatch<SetStateAction<DateRange | undefined>>;
  currencySymbol: string;
}) {
  // Use the selected date range for the API call
  const startDateStr = dateRange?.from
    ? format(dateRange.from, 'yyyy-MM-dd')
    : '';
  const endDateStr = dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : '';

  const params = {
    startDate: startDateStr,
    endDate: endDateStr,
    format: 'json',
  } as { startDate: string; endDate: string; format: string };

  const {
    data: failedPaymentsData,
    isLoading,
    isError,
  } = useReportingFailedPayments(params);

  const columns = useMemo(
    () => [
      failedPaymentColumnHelper.accessor(
        (row) => `${row.studentName} / ${row.parentName}`,
        {
          id: 'studentParent',
          header: 'Student/Parent',
          enableSorting: false,
          cell: ({ getValue }) => (
            <span className='font-medium'>{getValue()}</span>
          ),
        },
      ),
      failedPaymentColumnHelper.accessor('productName', {
        id: 'class',
        header: 'Class',
        enableSorting: false,
      }),
      failedPaymentColumnHelper.accessor('failedAmount', {
        id: 'amount',
        header: 'Amount',
        enableSorting: false,
        cell: ({ getValue }) => (
          <>
            {currencySymbol}
            {getValue().toFixed(2)}
          </>
        ),
      }),
      failedPaymentColumnHelper.accessor('failedDate', {
        id: 'date',
        header: 'Date',
        enableSorting: false,
        cell: ({ getValue }) => new Date(getValue()).toLocaleDateString(),
      }),
      failedPaymentColumnHelper.accessor(() => 'Failed', {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: () => <Badge variant='destructive'>Failed</Badge>,
      }),
    ],
    [currencySymbol],
  );
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  if (isLoading) {
    return <div>Loading failed payments...</div>;
  }
  if (isError) {
    return <div>Error loading failed payments.</div>;
  }

  const payments =
    typeof failedPaymentsData === 'object'
      ? Array.isArray(failedPaymentsData.failedPaymentDetails)
        ? failedPaymentsData.failedPaymentDetails
        : []
      : [];

  return (
    <EnrollioTable
      ariaLabel='Failed payment details'
      totalLabel='Failed payments'
      filterHeader={(table) => (
        <>
          <EnrollioTableSearch
            placeholder='Search students or parents...'
            value={
              (table.getColumn('studentParent')?.getFilterValue() as
                | string
                | undefined) ?? ''
            }
            onChange={(value) => {
              table.getColumn('studentParent')?.setFilterValue(value);
            }}
          />
          <DatePickerWithRange date={dateRange} setDate={setDateRange} />
        </>
      )}
      toolbar={(table) => <EnrollioTableColumnToggle table={table} />}
      columns={columns}
      data={payments}
      isUpdating={isLoading}
      getRowId={(row, index) =>
        `${row.failedDate}-${row.parentEmail}-${row.studentName}-${row.productName}-${index}`
      }
      sorting={sorting}
      onSortingChange={setSorting}
      columnFilters={columnFilters}
      onColumnFiltersChange={setColumnFilters}
      columnVisibility={columnVisibility}
      onColumnVisibilityChange={setColumnVisibility}
    />
  );
}
